import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Collapse,
} from "@mui/material";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import { useAuth } from "../contexts/AuthContext";
import { useSupportWidget } from "../hooks/useSupportWidget";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationDrawer from "../components/NotificationDrawer";
import { useRefresh } from "../contexts/RefreshContext";
import { useEffect, useState } from "react";
import useFcmToken from "../hooks/useFcmToken";
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
} from "../api/notificationService";
import theme from "../theme/theme";
import {
  AccessTime,
  AccountTree,
  AccountBalance,
  LocationOn,
  Architecture,
  Build,
  Inventory as InventoryIcon,
  ExpandLess,
  ExpandMore,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Store as StoreIcon,
  FormatListBulleted as ListIcon,
  PlaylistAdd as PlaylistAddIcon,
  SupportAgent as SupportAgentIcon,
  Dns as DnsIcon,
} from "@mui/icons-material";

const AdminLayout = () => {
  const { user } = useAuth();
  const { permissions } = useAuth();
  const { logout } = useAuth();

  // Load the Support Hub chat widget for the logged-in user (verified by phone).
  useSupportWidget();
  const [notifications, setNotifications] = useState({
    notifications: { data: [] },
    unread_count: 0,
  });
  const { refreshKey } = useRefresh();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(
    location.pathname.startsWith("/inventory"),
  );

  useFcmToken(); // Initialize FCM Logic

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await getNotifications();
        setNotifications(data);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };
    fetchNotifications();
  }, [refreshKey]);

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
      // Optimistic update or refresh
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleLogout = () => {
    // Clear the Support Hub widget session so the next user starts clean.
    localStorage.removeItem("hub_widget_session_rainbow");
    logout();
  };

  const menuItems = [
    {
      text: "Dashboard",
      path: "/",
      icon: <DashboardIcon />,
      permission: "dashboard.view",
    },
    {
      text: "Users",
      path: "/users",
      icon: <GroupIcon />,
      permission: "users.view",
    },
    {
      text: "Zones",
      path: "/zones",
      icon: <LocationOn />,
      permission: "users.view",
    },
    {
      text: "Banks",
      path: "/banks",
      icon: <AccountBalance />,
      permission: "users.view",
    },
    {
      text: "Attendance Summary",
      path: "/attendance-summary",
      icon: <AccessTime />,
      permission: "attendance.view",
    },
    {
      text: "Attendance",
      path: "/attendance",
      icon: <AccessTime />,
      permission: "attendance.view",
    },
    {
      text: "Employees",
      path: "/employees",
      icon: <PersonIcon />,
      permission: "employees.view",
    },
    {
      text: "Leads",
      path: "/leads",
      icon: <AccountTree />,
      permission: "leads.view",
    },
    {
      text: "Customers",
      path: "/customers",
      icon: <PeopleIcon />,
      permission: "users.view",
    },
    {
      text: "Projects",
      path: "/projects",
      icon: <Architecture />,
      permission: "leads.view",
    },
    {
      text: "Services",
      path: "/services",
      icon: <Build />,
      permission: "leads.view",
    },
    {
      text: "Invoices",
      path: "/invoices",
      icon: <ReceiptIcon />,
      permission: "dashboard.view",
    },
    {
      text: "Handover Letters",
      path: "/handover-letters",
      icon: <DescriptionIcon />,
      permission: "dashboard.view",
    },
    {
      text: "Support Tickets",
      path: "/support-tickets",
      icon: <SupportAgentIcon />,
      permission: "dashboard.view",
    },
    {
      text: "Inventory",
      icon: <InventoryIcon />,
      permission: "dashboard.view", // General permission for now
      children: [
        {
          text: "Categories",
          path: "/inventory/categories",
          icon: <CategoryIcon />,
        },
        { text: "Item Master", path: "/inventory/items", icon: <ListIcon /> },
        {
          text: "Warehouses",
          path: "/inventory/warehouses",
          icon: <StoreIcon />,
        },
        {
          text: "Stock Levels",
          path: "/inventory/stock",
          icon: <InventoryIcon />,
        },
        {
          text: "Initial Stock",
          path: "/inventory/initial-stock",
          icon: <PlaylistAddIcon />,
        },
      ],
    },
    {
      text: "Server",
      path: "/server",
      icon: <DnsIcon />,
      permission: "users.view",
    },
  ];
  const drawerWidth = 240;
  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "#f6f8f8",
        height: "100%",
        minHeight: "100vh",
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#0a1628",
          },
        }}
      >
        <Box p={2} sx={{ overflow: "hidden" }}>
          <Box>
            <Typography variant="h5" color="white">
              Rainbow Lifts
            </Typography>
            <Divider color="white" sx={{ marginY: 2 }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <List
              sx={{
                height: "calc(100vh - 180px)",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#555",
                  borderRadius: "20px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              {menuItems.map((item) => {
                if (!permissions.includes(item.permission)) return null;

                if (item.children) {
                  return (
                    <Box key={item.text}>
                      <ListItemButton
                        onClick={() => setInventoryOpen(!inventoryOpen)}
                        sx={{
                          color: "#fff",
                          "&:hover": { backgroundColor: "#0d8ca5" },
                          marginBottom: 1,
                          borderRadius: theme.button.borderRadius,
                        }}
                      >
                        <ListItemIcon sx={{ color: "#fff" }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                        {inventoryOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                      <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.children.map((child) => (
                            <ListItemButton
                              key={child.text}
                              component={NavLink}
                              to={child.path}
                              sx={{
                                color: "#fff",
                                pl: 4,
                                "&.active": { backgroundColor: "#0d8ca5" },
                                "&:hover": { backgroundColor: "#0d8ca5" },
                                marginBottom: 1,
                                borderRadius: theme.button.borderRadius,
                              }}
                            >
                              <ListItemIcon
                                sx={{ color: "#fff", minWidth: 40 }}
                              >
                                {child.icon}
                              </ListItemIcon>
                              <ListItemText primary={child.text} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                }

                return (
                  <ListItemButton
                    key={item.text}
                    component={NavLink}
                    to={item.path}
                    sx={{
                      color: "#fff",
                      "&.active": { backgroundColor: "#0d8ca5" },
                      "&:hover": { backgroundColor: "#0d8ca5" },
                      marginBottom: 1,
                      borderRadius: theme.button.borderRadius,
                    }}
                  >
                    <ListItemIcon sx={{ color: "#fff" }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                );
              })}
            </List>
            <Box>
              <Divider color="white" sx={{ marginY: 2 }} />
              <Button
                variant="contained"
                color="error"
                onClick={handleLogout}
                sx={{ width: "100%", borderRadius: "20px" }}
              >
                Logout
                <LogoutIcon sx={{ marginLeft: "10px" }} />
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Content */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
        <Box>
          <AppBar
            position="static"
            elevation={0}
            sx={{ borderBottom: "1px solid #e0e0e0" }}
          >
            <Toolbar sx={{ backgroundColor: "#fff" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography variant="h6" color="#0a1628" component="div">
                  Welcome, {user?.name || "User"}
                </Typography>

                <IconButton
                  onClick={() => setIsDrawerOpen(true)}
                  sx={{ color: "#0a1628" }}
                >
                  <Badge
                    color="secondary"
                    badgeContent={notifications?.unread_count || 0}
                    showZero
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>
        </Box>
        <Box sx={{ p: 3, overflow: "auto", height: "calc(100vh - 64px)" }}>
          <Outlet />
        </Box>
      </Box>
      <NotificationDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
      />
    </Box>
  );
};

export default AdminLayout;
